import { useState, useEffect } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { ThemeSwitch } from '@/components/theme-switch'
import { TopNav } from '@/components/layout/top-nav'
import { Search as SearchComponent } from '@/components/search'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { toast } from 'sonner'
import { PageSEOService } from '@/services/page-seo-service'
import { Loader2 } from 'lucide-react'

const topNav = [
    { title: 'Anasayfa', href: '/', isActive: false },
]

const STATIC_PAGES = [
    { path: '/', name: 'Anasayfa' },
    { path: '/hakkimizda', name: 'Hakkımızda' },
    { path: '/iletisim', name: 'İletişim' },
    { path: '/kariyer', name: 'Kariyer' },
    { path: '/uretim-sureci', name: 'Üretim Süreci' },
    { path: '/kalite-belgeleri', name: 'Kalite Belgeleri' },
    { path: '/sss', name: 'Sıkça Sorulan Sorular' },
    { path: '/kurumsal', name: 'Kurumsal' },
    { path: '/urunler', name: 'Ürünlerimiz' },
    { path: '/skg-blog', name: 'Blog' },
]

export default function SayfaSeoPage() {
    const [isLoading, setIsLoading] = useState(true)
    const [activeTab, setActiveTab] = useState('/')

    // Form states per tab
    const [formData, setFormData] = useState<Record<string, { meta_title: string; meta_description: string; meta_keywords: string }>>({})

    useEffect(() => {
        loadRecords()
    }, [])

    const loadRecords = async () => {
        setIsLoading(true)
        try {
            const data = await PageSEOService.getAll()

            const mapped: Record<string, any> = {}
            STATIC_PAGES.forEach(page => {
                const found = data.find((d: any) => d.pagePath === page.path)
                mapped[page.path] = {
                    meta_title: found?.meta_title || '',
                    meta_description: found?.meta_description || '',
                    meta_keywords: found?.meta_keywords || ''
                }
            })
            setFormData(mapped)
        } catch (error) {
            toast.error('SEO kayıtları alınırken bir hata oluştu.')
        } finally {
            setIsLoading(false)
        }
    }

    const handleSave = async (path: string) => {
        try {
            const data = formData[path]
            await PageSEOService.upsert({
                pagePath: path,
                ...data
            })
            toast.success(`${path} sayfasının SEO ayarları kaydedildi.`)
            await loadRecords()
        } catch (error) {
            toast.error('Kaydedilirken bir hata oluştu.')
        }
    }

    const handleChange = (path: string, field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [path]: {
                ...prev[path],
                [field]: value
            }
        }))
    }

    if (isLoading) {
        return (
            <>
                <Header>
                    <TopNav links={topNav} />
                    <div className="ml-auto flex items-center gap-2">
                        <SearchComponent />
                        <ThemeSwitch />
                        <ProfileDropdown />
                    </div>
                </Header>
                <Main>
                    <div className="flex h-[80vh] items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                </Main>
            </>
        )
    }

    return (
        <>
            <Header>
                <TopNav links={topNav} />
                <div className="ml-auto flex items-center gap-2">
                    <SearchComponent />
                    <ThemeSwitch />
                    <ProfileDropdown />
                </div>
            </Header>

            <Main>
                <div className="mb-4">
                    <h1 className="text-2xl font-bold tracking-tight">Sayfa Bazlı SEO Yönetimi</h1>
                    <p className="text-muted-foreground">
                        Web sitenizdeki statik (sabit) sayfaların Title, Description ve Keyword etiketlerini buradan düzenleyin.
                    </p>
                </div>

                {isLoading ? null : (
                    <div className="flex flex-col md:flex-row gap-6">
                        <Tabs orientation="vertical" value={activeTab} onValueChange={setActiveTab} className="flex w-full gap-6">
                            <div className="w-1/4 max-w-sm rounded-lg border bg-card text-card-foreground shadow-sm overflow-hidden p-2">
                                <TabsList className="flex flex-col h-auto bg-transparent items-stretch w-full">
                                    {STATIC_PAGES.map(page => (
                                        <TabsTrigger
                                            key={page.path}
                                            value={page.path}
                                            className="justify-start px-4 py-3 text-left data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
                                        >
                                            <div className="flex flex-col items-start gap-1">
                                                <span className="font-semibold">{page.name}</span>
                                                <span className="text-xs text-muted-foreground opacity-70 font-mono">{page.path}</span>
                                            </div>
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                            </div>

                            <div className="flex-1">
                                {STATIC_PAGES.map(page => (
                                    <TabsContent key={page.path} value={page.path} className="mt-0 border-0 outline-none">
                                        <Card className="shadow-sm border-0 bg-white dark:bg-zinc-900/50">
                                            <CardHeader>
                                                <CardTitle>{page.name} Sayfası SEO Ayarları</CardTitle>
                                                <CardDescription>
                                                    Arama motorlarında (Google) {page.name.toLowerCase()} sayfası için görünecek etiketleri tanımlayın. Boş bırakılan alanlar Site Genel Ayarlarından (Fallback) okuyacaktır.
                                                </CardDescription>
                                            </CardHeader>
                                            <CardContent className="space-y-6">
                                                <div className="space-y-2">
                                                    <Label>Meta Title (Başlık)</Label>
                                                    <Input
                                                        placeholder={`Örn: SKG Metal - ${page.name}`}
                                                        value={formData[page.path]?.meta_title || ''}
                                                        onChange={e => handleChange(page.path, 'meta_title', e.target.value)}
                                                    />
                                                    <p className="text-xs text-muted-foreground mt-1">Önerilen uzunluk: 50-60 karakter. Sekme başlığında ve Google arama sonuçlarında (mavi link) görünür.</p>
                                                </div>

                                                <div className="space-y-2">
                                                    <Label>Meta Description (Açıklama)</Label>
                                                    <Textarea
                                                        placeholder="Bu sayfanın kısa ve açıklayıcı metnini girin."
                                                        rows={3}
                                                        value={formData[page.path]?.meta_description || ''}
                                                        onChange={e => handleChange(page.path, 'meta_description', e.target.value)}
                                                    />
                                                    <p className="text-xs text-muted-foreground mt-1">Önerilen uzunluk: 150-160 karakter. Arama sonuçlarında linkin altındaki siyah metindir.</p>
                                                </div>

                                                <div className="space-y-2">
                                                    <Label>Meta Keywords (Anahtar Kelimeler)</Label>
                                                    <Input
                                                        placeholder="üretim, metal, iletişim, fabrika vs."
                                                        value={formData[page.path]?.meta_keywords || ''}
                                                        onChange={e => handleChange(page.path, 'meta_keywords', e.target.value)}
                                                    />
                                                    <p className="text-xs text-muted-foreground mt-1">Anahtar kelimeleri virgülle ayırarak yazın. Artık çoğu arama motoru tarafından dikkate alınmaz, ancak opsiyonel olarak eklenebilir.</p>
                                                </div>
                                            </CardContent>
                                            <CardFooter className="bg-muted/30 pt-4 rounded-b-xl border-t">
                                                <Button className="ml-auto" onClick={() => handleSave(page.path)}>Kaydet</Button>
                                            </CardFooter>
                                        </Card>
                                    </TabsContent>
                                ))}
                            </div>
                        </Tabs>
                    </div>
                )}
            </Main>
        </>
    )
}

export const Route = createFileRoute('/_authenticated/sayfa-seo/')({
    component: SayfaSeoPage,
})
